import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Alert, MenuItem, IconButton, Chip, Card, CardContent, Stack, TablePagination, Divider } from '@mui/material';
import { Edit, Block, CheckCircle, Business } from '@mui/icons-material';
import { tenantService } from '../services/api';
import { usePagination } from '../hooks/usePagination';

// Platform-admin screen: every company hosted on this deployment. Only reachable for accounts the
// server lists in Tenancy:PlatformAdmins (the sidebar hides it for everyone else, and the API
// rejects the calls anyway).

const emptyForm = {
  name: '', description: '', subdomain: '', contactEmail: '', contactPhone: '',
  companyAddress: '', companyWebsite: '', primaryColor: '#1976d2', secondaryColor: '#dc004e',
  subscriptionPlan: 'Basic', maxEmployees: 50, subscriptionEndDate: '',
  adminFullName: '', adminEmail: '', adminUsername: '', adminPassword: '',
};

// <input type="date"> works in YYYY-MM-DD; the server expects an instant. A subscription is
// treated as valid THROUGH the chosen day, so send the end of that day.
const toDateInput = (iso?: string | null) => (iso ? iso.substring(0, 10) : '');
const toEndOfDay = (date: string) => (date ? `${date}T23:59:59` : null);

const TenantManagement: React.FC = () => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [isActive, setIsActive] = useState(true);
  const [credentials, setCredentials] = useState<any>(null);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const set = (field: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [field]: e.target.value });

  const flash = (text: string, severity: 'success' | 'error' = 'success') => {
    setMessage({ text, severity });
    if (severity === 'success') setTimeout(() => setMessage(null), 4000);
  };

  const load = async () => {
    try {
      setTenants(await tenantService.getAll());
    } catch (error: any) {
      flash(error.response?.data?.message || 'Could not load companies', 'error');
    }
  };

  useEffect(() => { load(); }, []);

  const commonFields = () => ({
    name: form.name, description: form.description, subdomain: form.subdomain || null,
    contactEmail: form.contactEmail, contactPhone: form.contactPhone,
    companyAddress: form.companyAddress, companyWebsite: form.companyWebsite,
    primaryColor: form.primaryColor, secondaryColor: form.secondaryColor,
    subscriptionPlan: form.subscriptionPlan, maxEmployees: Number(form.maxEmployees) || 0,
    subscriptionEndDate: toEndOfDay(form.subscriptionEndDate),
  });

  const handleCreate = async () => {
    try {
      const result = await tenantService.create({
        ...commonFields(),
        adminFullName: form.adminFullName, adminEmail: form.adminEmail,
        adminUsername: form.adminUsername || null, adminPassword: form.adminPassword || null,
      });
      setCreateOpen(false);
      setForm({ ...emptyForm });
      setCredentials(result);
      load();
    } catch (error: any) {
      flash(error.response?.data?.message || 'Error creating company', 'error');
    }
  };

  const openEdit = (t: any) => {
    setSelected(t);
    setIsActive(t.isActive);
    setForm({
      ...emptyForm,
      name: t.name, description: t.description || '', subdomain: t.subdomain || '',
      contactEmail: t.contactEmail || '', contactPhone: t.contactPhone || '',
      companyAddress: t.companyAddress || '', companyWebsite: t.companyWebsite || '',
      primaryColor: t.primaryColor || '#1976d2', secondaryColor: t.secondaryColor || '#dc004e',
      subscriptionPlan: t.subscriptionPlan || 'Basic', maxEmployees: t.maxEmployees,
      subscriptionEndDate: toDateInput(t.subscriptionEndDate),
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!selected) return;
    try {
      await tenantService.update(selected.id, { ...commonFields(), logoUrl: selected.logoUrl, isActive });
      setEditOpen(false);
      flash('Company updated');
      load();
    } catch (error: any) {
      flash(error.response?.data?.message || 'Error updating company', 'error');
    }
  };

  const toggleActive = async (t: any) => {
    try {
      if (t.isActive) {
        await tenantService.deactivate(t.id);
        flash(`${t.name} deactivated — its users are locked out; all data is kept.`);
      } else {
        await tenantService.update(t.id, {
          name: t.name, description: t.description, subdomain: t.subdomain,
          contactEmail: t.contactEmail, contactPhone: t.contactPhone,
          companyAddress: t.companyAddress, companyWebsite: t.companyWebsite, logoUrl: t.logoUrl,
          primaryColor: t.primaryColor, secondaryColor: t.secondaryColor, subscriptionPlan: t.subscriptionPlan,
          maxEmployees: t.maxEmployees, subscriptionEndDate: t.subscriptionEndDate, isActive: true,
        });
        flash(`${t.name} reactivated`);
      }
      load();
    } catch (error: any) {
      flash(error.response?.data?.message || 'Could not change status', 'error');
    }
  };

  const { pagedRows, paginationProps } = usePagination(tenants);

  const isExpired = (t: any) => !!t.subscriptionEndDate && new Date(t.subscriptionEndDate) < new Date();
  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.isActive && !isExpired(t)).length,
    locked: tenants.filter(t => !t.isActive || isExpired(t)).length,
    users: tenants.reduce((sum, t) => sum + (t.activeUsers || 0), 0),
  };

  const companyFields = (editing: boolean) => (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField fullWidth required label="Company Name" value={form.name} onChange={set('name')} />
        <TextField fullWidth label="Subdomain" value={form.subdomain} onChange={set('subdomain')}
          helperText={editing ? 'Changing this changes the company’s web address' : 'e.g. acme → acme.<your domain>. Optional.'} />
      </Box>
      <TextField fullWidth label="Description" value={form.description} onChange={set('description')} multiline rows={2} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField fullWidth label="Contact Email" type="email" value={form.contactEmail} onChange={set('contactEmail')} />
        <TextField fullWidth label="Contact Phone" value={form.contactPhone} onChange={set('contactPhone')} />
      </Box>
      <TextField fullWidth label="Company Address" value={form.companyAddress} onChange={set('companyAddress')} />
      <TextField fullWidth label="Company Website" value={form.companyWebsite} onChange={set('companyWebsite')} />
      <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Plan &amp; limits</Typography></Divider>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
        <TextField select fullWidth label="Subscription Plan" value={form.subscriptionPlan} onChange={set('subscriptionPlan')}>
          <MenuItem value="Basic">Basic</MenuItem>
          <MenuItem value="Premium">Premium</MenuItem>
          <MenuItem value="Enterprise">Enterprise</MenuItem>
        </TextField>
        <TextField fullWidth label="Max active employees" type="number" value={form.maxEmployees} onChange={set('maxEmployees')}
          helperText="0 = unlimited" />
        <TextField fullWidth label="Subscription valid through" type="date" InputLabelProps={{ shrink: true }}
          value={form.subscriptionEndDate} onChange={set('subscriptionEndDate')} helperText="Empty = never expires" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField fullWidth label="Primary Color" type="color" value={form.primaryColor} onChange={set('primaryColor')} />
        <TextField fullWidth label="Secondary Color" type="color" value={form.secondaryColor} onChange={set('secondaryColor')} />
      </Box>
    </>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
        Companies
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card><CardContent><Typography color="textSecondary" gutterBottom>Companies</Typography><Typography variant="h4">{stats.total}</Typography></CardContent></Card>
        <Card><CardContent><Typography color="textSecondary" gutterBottom>Active</Typography><Typography variant="h4" color="success.main">{stats.active}</Typography></CardContent></Card>
        <Card><CardContent><Typography color="textSecondary" gutterBottom>Locked out</Typography><Typography variant="h4" color="error.main">{stats.locked}</Typography></CardContent></Card>
        <Card><CardContent><Typography color="textSecondary" gutterBottom>Active users (all)</Typography><Typography variant="h4">{stats.users}</Typography></CardContent></Card>
      </Box>

      <Button variant="contained" onClick={() => { setForm({ ...emptyForm }); setCreateOpen(true); }} sx={{ mb: 2 }}>
        Add Company
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Subdomain</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Valid through</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.primaryColor || '#1976d2' }} />
                    <Typography variant="body2" fontWeight="bold">{t.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Chip label={t.subdomain || '—'} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Typography variant="body2">{t.contactEmail}</Typography>
                  <Typography variant="caption" color="textSecondary">{t.contactPhone}</Typography>
                </TableCell>
                <TableCell><Chip label={t.subscriptionPlan} size="small" color={t.subscriptionPlan === 'Enterprise' ? 'primary' : t.subscriptionPlan === 'Premium' ? 'secondary' : 'default'} /></TableCell>
                <TableCell>{t.activeUsers} / {t.maxEmployees > 0 ? t.maxEmployees : '∞'}</TableCell>
                <TableCell>{t.subscriptionEndDate ? new Date(t.subscriptionEndDate).toLocaleDateString() : 'Never expires'}</TableCell>
                <TableCell>
                  {!t.isActive
                    ? <Chip label="Deactivated" size="small" color="error" />
                    : isExpired(t)
                      ? <Chip label="Expired" size="small" color="warning" />
                      : <Chip label="Active" size="small" color="success" />}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(t)} title="Edit"><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color={t.isActive ? 'error' : 'success'} onClick={() => toggleActive(t)} title={t.isActive ? 'Deactivate' : 'Reactivate'}>
                    {t.isActive ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {tenants.length > 0 && <TablePagination {...paginationProps} />}
      </TableContainer>

      {/* Create */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Company</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {companyFields(false)}
            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">First administrator</Typography></Divider>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth required label="Admin full name" value={form.adminFullName} onChange={set('adminFullName')} />
              <TextField fullWidth required label="Admin email" type="email" value={form.adminEmail} onChange={set('adminEmail')} />
              <TextField fullWidth label="Admin username" value={form.adminUsername} onChange={set('adminUsername')} helperText="Optional — generated from the name" />
              <TextField fullWidth label="Admin password" value={form.adminPassword} onChange={set('adminPassword')} helperText="Optional — a strong one is generated" />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!form.name || !form.adminFullName || !form.adminEmail}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit {selected?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {companyFields(true)}
            <TextField select fullWidth label="Status" value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Deactivated (users locked out, data kept)</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* One-time credentials for the new company's first admin */}
      <Dialog open={!!credentials} onClose={() => setCredentials(null)}>
        <DialogTitle>{credentials?.tenant?.name} created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy these now and pass them to the company&apos;s administrator. The password is not stored and cannot be shown again.
          </Alert>
          {credentials && !credentials.keycloakSynced && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Single sign-on was not reachable, so the account will be activated automatically the first time the admin signs in.
            </Alert>
          )}
          <Typography variant="body2">Username: <b>{credentials?.adminUsername}</b></Typography>
          <Typography variant="body2">Password: <b>{credentials?.adminPassword}</b></Typography>
          <Typography variant="body2">Email: {credentials?.adminEmail}</Typography>
          {credentials?.tenant?.subdomain && (
            <Typography variant="body2" sx={{ mt: 1 }}>Portal subdomain: <b>{credentials.tenant.subdomain}</b></Typography>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setCredentials(null)} variant="contained">Done</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantManagement;
